import React, { useCallback, useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import SortableTable from "../../Components/Table/SortableTable";
import Breadcrumb from "../../Components/Navigation/Breadcrumbs/Breadcrumbs";
import { Button, Dialog, Grid } from "@material-ui/core";
import Signifier from "../../Components/Signifier";
import EmailTemplateModal from "../../Components/Modal/EmailTemplateModal";
import EmailTemplateTableCell from "../../Components/Table/EmailTemplateTableCell";
import AutoCompleteComponent from "../../Components/AutoComplete/AutoComplete";
import { getAllClients } from "../../Services/API/ClientManagement";
import { getEmailTemplates, getEmailTypes } from "../../Services/API/EmailTemplateService";
import axios from "axios";

function EmailTemplatesPage() {
  const useStyles = makeStyles((theme) => ({
    root: {
      width: "90%",
      marginBottom: "50px",
      [theme.breakpoints.down("sm")]: {
        width: "100%",
      },
    },
    addButton: {
      marginTop: "25px",
      [theme.breakpoints.down("sm")]: {
        marginTop: "0px",
      },
    },
    autocomplete: {
      width: "20%",
      paddingBottom: "20px",
      [theme.breakpoints.down("sm")]: {
        width: "100%",
      },
    },
    hint: {
      position: "absolute",
      left: 0,
      right: 0,
      top: 0,
      bottom: 0,
      marginTop: "auto",
      marginBottom: "auto",
      marginLeft: "auto",
      marginRight: "auto",
      width: "300px",
      height: "300px",
      fontSize: "32px",
      textAlign: "center",
      color: "rgba(0,0,0,.6)",
      [theme.breakpoints.down("sm")]: {
        position: "unset",
      },
    },
  }));

  const classes = useStyles();

  const [showSignifier, setShowSignifier] = useState(false);
  const [showError, setShowError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [data, setData] = useState([]);
  const [allClients, setAllClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [emailTypes, setEmailTypes] = useState([]);
  const [selectedClientID, setSelectedClientID] = useState("");
  axios.defaults.headers.common["client"] = selectedClientID;

  const columns = [
    { label: "Name", propName: "name" },
    { label: "Email Type", propName: "emailTypeName" },
    { label: "Subject", propName: "subject" },
    { label: "Date Created", propName: "dateCreated" },
    { label: "Date Last Updated", propName: "dateLastUpdated" },
  ];

  const handleClientChange = (event, value) => {
    if (value === null) {
      setSelectedClient(null);
      return;
    }
    setSelectedClient(value);
    for (const x in allClients) {
      if (allClients[x].shortName === value.shortName) {
        setSelectedClientID(allClients[x].id);
      }
    }
  };

  const compare = (a, b) => {
    if (a < b) {
      return -1;
    }
    if (a > b) {
      return 1;
    }
    return 0;
  };

  const convertDate = (date) => {
    if (!date) return null;
    const parsed = new Date(date);
    return isNaN(parsed.getTime()) ? null : parsed.toLocaleDateString("en-US");
  };

  const addEmailType = useCallback(
    (data) => {
      for (const i in data) {
        data[i].emailTypes = emailTypes;
        const contentType = data[i].emailTemplateType;
        for (const x in emailTypes) {
          if (emailTypes[x].id === contentType) {
            data[i].emailTypeName = emailTypes[x].name;
            break;
          }
        }
      }
      return data;
    },
    [emailTypes],
  );

  useEffect(() => {
    getEmailTypes()
      .then((res) => {
        setEmailTypes(res);
        return getAllClients().then((response) => {
          response.sort((a, b) => {
            const order = compare(a.shortName, b.shortName);
            if (order !== 0) return order;
            return a[1] - b[1];
          });
          setAllClients(response);
        });
      })
      .catch((err) => console.error("Failed to load email types/clients:", err));
  }, []);

  const loadTemplates = useCallback(() => {
    if (selectedClient === null) return;
    setLoading(true);
    setError(false);
    return getEmailTemplates(selectedClient.id)
      .then((res) => {
        for (const i in res) {
          res[i].dateCreated = convertDate(res[i].dateCreated);
          res[i].dateLastUpdated = convertDate(res[i].dateLastUpdated);
        }
        res = addEmailType(res);
        setData(res);
      })
      .catch((err) => {
        console.error("Failed to load email templates:", err);
        setError(true);
      })
      .finally(() => setLoading(false));
  }, [addEmailType, selectedClient]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  return (
    <>
      <Signifier
        showSignifier={showSignifier}
        setShowSignifier={setShowSignifier}
        message="Account Added"
        error={showError}
      />
      <div className="page-container">
        <Breadcrumb />
        <h1 className="page-title">Email Templates</h1>
        <div className={classes.root}>
          <div className={classes.autocomplete}>
            <AutoCompleteComponent
              dropdownItems={allClients}
              propertyName="shortName"
              label="Search for clients..."
              onChange={handleClientChange}
            />
          </div>
          {selectedClient !== null ? (
            <>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={6}>
                  <Button
                    color="primary"
                    className={classes.addButton}
                    style={{ color: "white", marginBottom: "10px" }}
                    type="submit"
                    variant="contained"
                    onClick={() => setOpenModal(true)}
                  >
                    Add Template
                  </Button>
                </Grid>
              </Grid>
              <SortableTable
                columns={columns}
                tableData={data}
                loading={loading}
                error={error}
                onRetry={loadTemplates}
                rowComponent={EmailTemplateTableCell}
                defaultSortIndex={0}
              />
            </>
          ) : (
            <div className={classes.hint}>
              <span>Select a client</span>
            </div>
          )}
        </div>
      </div>
      <Dialog open={openModal} fullWidth maxWidth="lg" onClose={() => setOpenModal(false)}>
        <EmailTemplateModal
          openModal={setOpenModal}
          setData={setData}
          setShowSignifier={setShowSignifier}
          setShowError={setShowError}
          emailTypes={emailTypes}
          clientId={selectedClientID}
        />
      </Dialog>
    </>
  );
}

export default EmailTemplatesPage;
